/**
 * THE RETRY SCHEDULE. Exponential, jittered, capped, and bounded in total attempts.
 * ================================================================================
 *
 * Four properties, each with a reason and each asserted in `test/backoff.test.ts`:
 *
 *  1. **Exponential** — `BASE_DELAY_MS * 2^(attempts-1)`. A technician who loses signal in a metal
 *     locker bay regains it in seconds; a technician in a rural dead zone regains it in hours. One
 *     schedule has to serve both, so the first retry is fast and the tenth is not.
 *
 *  2. **Capped at {@link MAX_DELAY_MS}** (5 minutes). Uncapped doubling reaches days, and a
 *     submission that will not be retried until Thursday is a lost submission with a friendly
 *     status. Five minutes is short enough that a van pulling into signal drains the queue during
 *     the same visit and long enough that a device with 40 queued items is not a load generator.
 *
 *  3. **Jittered — and this is the property with teeth.** Every device in a depot loses and regains
 *     the same wifi at the same moment. Without jitter their retries stay in lockstep forever: a
 *     synchronised thundering herd against an API Gateway with a per-stage throttle, where the 429s
 *     the herd causes reschedule the whole herd to the same next instant. **Equal jitter** —
 *     `[delay/2, delay]` — rather than full jitter `[0, delay]`, because full jitter can schedule a
 *     retry almost immediately after a failure and the first retry is the one most likely to hit the
 *     same broken connection. The lower bound preserves "back off at all".
 *
 *  4. **Bounded in ATTEMPTS, not just in delay** ({@link MAX_ATTEMPTS}). An item that keeps failing
 *     for a reason we classify as retryable — a 500, or a 503 naming an unbuilt dependency like
 *     `PRD-0.12-salesforce-identity` — must eventually stop and become visibly `failed` rather than
 *     retrying in the background forever. Forever-retrying is indistinguishable, on a phone, from
 *     working. 12 attempts at this schedule is roughly 50 minutes of trying; after that the item
 *     needs a human, and the UI says so.
 *
 * `computeBackoffDelay` is pure and takes its randomness as a parameter, so the test asserts the
 * real schedule with a controlled `rng` instead of asserting a mock.
 */

export const BASE_DELAY_MS = 1_000;
export const MAX_DELAY_MS = 5 * 60 * 1_000;
export const MAX_ATTEMPTS = 12;

/**
 * The delay before attempt number `attempts + 1`, given that `attempts` have already failed.
 *
 * @param attempts number of failed attempts so far (1 after the first failure).
 * @param rng a `[0, 1)` source; defaults to `Math.random`. Injected in tests.
 */
export function computeBackoffDelay(attempts: number, rng: () => number = Math.random): number {
  // `attempts` below 1 would make the exponent negative and produce a sub-base delay; clamped
  // rather than thrown, because a scheduling helper must not be able to take down the flusher.
  const failedAttempts = Math.max(1, Math.floor(attempts));
  // `2 ** 1024` is Infinity in IEEE-754 doubles; the exponent is clamped so the arithmetic stays
  // finite before `Math.min` sees it. Otherwise `Infinity * 0.5` in the jitter step is NaN and the
  // item gets `nextAttemptAt: NaN`, which no index can order and no flusher would ever pick up.
  const exponent = Math.min(failedAttempts - 1, 40);
  const uncapped = BASE_DELAY_MS * 2 ** exponent;
  const capped = Math.min(uncapped, MAX_DELAY_MS);
  // Equal jitter: half fixed, half random. Bounded in [capped/2, capped].
  return Math.round(capped / 2 + rng() * (capped / 2));
}

/** Has this item exhausted the schedule? */
export function attemptsExhausted(attempts: number): boolean {
  return attempts >= MAX_ATTEMPTS;
}
